// The weekly STATEMENT OF HOURS on the office timesheet page (Session AVA). Hours only, never money: it is what the
// subcontractor writes their own weekly invoice from, and it states that it is not an invoice. The office generates it
// once the week is APPROVED (never automatically), and releases it to the subcontractor the engineer is linked to: a
// download on the Subcontractor Portal, or a Gmail DRAFT with the PDF attached for a person to send. Every generation
// and release is audited on the server. Withdraw takes a portal release away (RULE 1's way back).
//
// Each consequential action confirms through the branded two-step ConfirmButton naming who it reaches (the AEH rule).
// Shared components only (Button, ConfirmButton through buttonClasses, Loader, lib/format dates): no raw palette class.
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type TimesheetStatementView } from "../lib/api";
import { Button, buttonClasses } from "./Button";
import { ConfirmButton } from "./ConfirmButton";
import { Loader } from "./Loader";
import { formatDate } from "../lib/format";

function hm(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function TimesheetStatementPanel({ timesheetId, approved }: { timesheetId: string | null; approved: boolean }) {
  const [view, setView] = useState<TimesheetStatementView | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!timesheetId || !approved) return;
    setState("loading");
    try {
      setView(await api.timesheets.statement(timesheetId));
      setState("idle");
    } catch {
      setState("error");
    }
  }, [timesheetId, approved]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setMessage({ tone: "ok", text: done });
      await load();
    } catch (err) {
      setMessage({ tone: "error", text: err instanceof ApiError && err.message ? err.message : "That did not work just now. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  const s = view?.statement;
  const sub = view?.subcontractor ?? null;
  const confirmClass = buttonClasses("secondary", "sm");

  return (
    <section aria-label="Statement of hours" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm">
      <p className="mb-1 font-menu text-xs uppercase tracking-widest text-ele-blue">Statement of hours</p>
      <p className="text-ele-body">
        Hours only, never money. The subcontractor writes their own weekly invoice from it; it says it is not an invoice and shows no amounts.
      </p>

      {!approved || !timesheetId ? (
        <p className="mt-2 text-ele-strong">The statement is generated once the week is approved.</p>
      ) : state === "loading" && !view ? (
        <Loader label="Preparing the statement" shape="inline" className="mt-2" />
      ) : state === "error" && !view ? (
        <p role="alert" className="mt-2 text-ele-strong">
          The statement could not be loaded just now. Reload the page.
        </p>
      ) : view && s ? (
        <div className="mt-2 space-y-2">
          <p className="text-ele-strong">
            {s.totals.jobs === 0 ? "No job hours on this week." : `${s.totals.jobs} job ${s.totals.jobs === 1 ? "line" : "lines"}, ${hm(s.totals.totalMinutes)} in total (on site ${hm(s.totals.onSiteMinutes)}, travel ${hm(s.totals.travelMinutes)}).`}
            {s.totals.flatFeeVisits ? ` ${s.totals.flatFeeVisits} flat fee ${s.totals.flatFeeVisits === 1 ? "visit" : "visits"}.` : ""}
            {s.toBeConfirmed.length ? ` ${s.toBeConfirmed.length} ${s.toBeConfirmed.length === 1 ? "visit is" : "visits are"} listed as to be confirmed.` : ""}
          </p>
          <p className="text-ele-body">
            {sub ? `For ${sub.company}. ` : ""}
            {view.release.inForce
              ? `On the Subcontractor Portal since ${formatDate(view.release.releasedAt!)}${view.release.releasedByName ? `, released by ${view.release.releasedByName}` : ""}.`
              : view.release.supersededByApproval
                ? "Released before the week was approved again, so it is not on the portal: release it again to show the corrected week."
                : "Not on the Subcontractor Portal."}
            {view.release.emailDraftedAt ? ` Last put in a Gmail draft on ${formatDate(view.release.emailDraftedAt)}.` : ""}
          </p>
          {view.refusals.portal && <p className="text-ele-body">{view.refusals.portal}</p>}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void run(() => api.timesheets.downloadStatement(timesheetId, view.filename), "Statement downloaded.")}
            >
              Download PDF
            </Button>
            {!view.refusals.portal && sub && !view.release.inForce && (
              <ConfirmButton
                onConfirm={() => void run(() => api.timesheets.releaseStatement(timesheetId, "portal"), `Released to ${sub.company} on the Subcontractor Portal.`)}
                busy={busy}
                busyLabel="Releasing..."
                label="Release on the portal"
                confirmLabel={`Release it to ${sub.company} on the portal?`}
                className={confirmClass}
              />
            )}
            {view.release.releasedAt && (
              <ConfirmButton
                onConfirm={() => void run(() => api.timesheets.withdrawStatement(timesheetId), "Withdrawn from the Subcontractor Portal.")}
                busy={busy}
                busyLabel="Withdrawing..."
                label="Withdraw from the portal"
                confirmLabel="Withdraw it from the portal?"
                className={buttonClasses("ghost", "sm")}
              />
            )}
            {!view.refusals.emailDraft && sub && (
              <ConfirmButton
                onConfirm={() => void run(() => api.timesheets.releaseStatement(timesheetId, "email_draft"), `Put in Gmail Drafts to ${sub.email}. Review and send it from Gmail.`)}
                busy={busy}
                busyLabel="Drafting..."
                label="Put in a Gmail draft"
                confirmLabel={`Draft an email to ${sub.email}?`}
                className={confirmClass}
              />
            )}
          </div>
          {message && (
            <p role={message.tone === "error" ? "alert" : "status"} className="text-ele-strong">
              {message.text}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
